import { writable } from 'svelte/store';
import { api } from '../api/client';

// MessageHandler receives a message's payload, the topic it arrived on, and
// its type, such as `location` or `places` on a group topic.
type MessageHandler = (data: unknown, topic: string, type: string) => void;

// topicMatches mirrors the backend broker's segment matcher: "*" consumes one
// or more colon-separated segments, so "group:*" matches "group:<id>".
function topicMatches(pattern: string, topic: string): boolean {
  if (pattern === topic) return true;
  if (!pattern.includes('*')) return false;
  const pat = pattern.split(':');
  const top = topic.split(':');
  const match = (pi: number, ti: number): boolean => {
    while (pi < pat.length && ti < top.length) {
      if (pat[pi] === '*') {
        for (let consumed = 1; consumed <= top.length - ti; consumed++) {
          if (match(pi + 1, ti + consumed)) return true;
        }
        return false;
      }
      if (pat[pi] !== top[ti]) return false;
      pi++;
      ti++;
    }
    return pi === pat.length && ti === top.length;
  };
  return match(0, 0);
}

interface WsState {
  connected: boolean;
  reconnecting: boolean;
}

function createWebSocketStore() {
  const { subscribe, set } = writable<WsState>({
    connected: false,
    reconnecting: false,
  });

  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  const subscriptions = new Map<string, Set<MessageHandler>>();
  // reconnectHandlers run when the socket opens again after a drop. Messages
  // sent meanwhile are lost, so pages read again what they show live.
  const reconnectHandlers = new Set<() => void>();
  // opened is whether this identity's socket has been open before.
  let opened = false;
  let reconnectAttempt = 0;
  const maxReconnectDelay = 30000;
  // The token the open socket authenticated with. It rides the URL, so a
  // sign-in or sign-out has to be reconnected rather than sent down the wire.
  let connectedToken: string | null = null;

  function wsUrl(token: string): string {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
  }

  async function connect() {
    if (ws && ws.readyState !== WebSocket.CLOSED) return;
    // The server refuses anonymous sockets, so there is nothing to open until
    // someone signs in. An expired token would be refused the same way, so it
    // is refreshed first.
    let token: string | null;
    try {
      token = await api.freshToken();
    } catch {
      scheduleReconnect();
      return;
    }
    // A sign-out or another connect may have happened during the refresh.
    if (!token || token !== api.getToken() || (ws && ws.readyState !== WebSocket.CLOSED)) return;

    connectedToken = token;
    // Handlers close over their own socket and stand down once it is no longer
    // the current one: a deliberate close, and the old socket of a reconnect,
    // both land here while a replacement is already opening.
    const sock = new WebSocket(wsUrl(token));
    ws = sock;

    let everOpen = false;
    sock.onopen = () => {
      everOpen = true;
      // A first open after failed attempts follows page loads that may have
      // failed too, so it reads again as a reconnect does.
      const missed = opened || reconnectAttempt > 0;
      reconnectAttempt = 0;
      set({ connected: true, reconnecting: false });

      // Re-subscribe to all active topics.
      for (const topic of subscriptions.keys()) {
        sock.send(JSON.stringify(subscribeFrame(topic)));
      }
      if (missed) {
        for (const handler of reconnectHandlers) handler();
      }
      opened = true;

      pingTimer = setInterval(() => {
        sock.send(JSON.stringify({ type: 'ping' }));
      }, 30000);
    };

    sock.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.topic) {
          // Dispatch to exact and wildcard subscriptions.
          for (const [pattern, handlers] of subscriptions) {
            if (!topicMatches(pattern, msg.topic)) continue;
            for (const handler of handlers) {
              handler(msg.payload, msg.topic, msg.type);
            }
          }
        }
      } catch {
        // Ignore malformed messages.
      }
    };

    sock.onclose = () => {
      if (ws !== sock) return;
      if (pingTimer) clearInterval(pingTimer);
      set({ connected: false, reconnecting: true });
      // A socket refused before it opened may mean the sign-in ended while
      // its token still reads as good, as after signing out elsewhere. The
      // socket cannot see why, so a request asks: one the server refuses
      // sends the tab to sign in, and an unreachable server changes nothing.
      if (!everOpen) api.get('/auth/me').catch(() => {});
      scheduleReconnect();
    };

    sock.onerror = () => {
      sock.close();
    };
  }

  function subscribeFrame(topic: string): Record<string, string> {
    return { type: 'subscribe', topic };
  }

  function scheduleReconnect() {
    if (reconnectTimer) return;
    const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), maxReconnectDelay);
    reconnectAttempt++;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  function disconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
    ws?.close();
    ws = null;
    connectedToken = null;
    opened = false;
    set({ connected: false, reconnecting: false });
  }

  return {
    subscribe,

    connect,

    disconnect,

    // reconnect reopens the socket when the identity it was opened with has
    // changed. Signing in or out swaps which topics the server will allow, and
    // the token rides the URL, so an open socket keeps answering as whoever
    // opened it.
    reconnect() {
      if (ws && connectedToken === api.getToken()) return;
      disconnect();
      reconnectAttempt = 0;
      connect();
    },

    // onReconnect runs handler each time the socket opens again after a
    // drop, and returns a function that stops it.
    onReconnect(handler: () => void) {
      reconnectHandlers.add(handler);
      return () => {
        reconnectHandlers.delete(handler);
      };
    },

    subscribeTopic(topic: string, handler: MessageHandler) {
      if (!subscriptions.has(topic)) {
        subscriptions.set(topic, new Set());
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(subscribeFrame(topic)));
        }
      }
      subscriptions.get(topic)!.add(handler);

      return () => {
        const handlers = subscriptions.get(topic);
        if (handlers) {
          handlers.delete(handler);
          if (handlers.size === 0) {
            subscriptions.delete(topic);
            if (ws?.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'unsubscribe', topic }));
            }
          }
        }
      };
    },
  };
}

export const websocket = createWebSocketStore();
